import { MouseEvent, useCallback, useMemo } from "react";

import { getFieldCropRotation } from "services";

import {
  useFeatureFlag,
  useGetAllFieldPracticesByFarm,
  useGetFarms,
  useGetPracticeCrops,
  useListSoilSightProjects,
  useTranslation,
  useUserContext,
} from "utils";

import { FEATURE_FLAGS } from "consts";

import { CROP_TYPE_FCPO } from "models";

import { Message as message } from "components";

import { useSoilSightFieldImportSubmit } from "pages/app/scenario-analysis/hooks/useSoilSightFieldImportSubmit";
import { useSoilSightFieldImportViaOnboarding } from "pages/app/scenario-analysis/hooks/useSoilSightFieldImportViaOnboarding";
import { useSoilSightScenariosAccessBase } from "pages/app/scenario-analysis/hooks/useSoilSightScenariosAccess";
import { reconcileYearsResidueLocks } from "pages/app/scenario-analysis/utils/cropYearHelpers";
import { mapCropRotationToSoilSightYears, mapFieldCropsToSoilSightYears } from "pages/app/scenario-analysis/utils/fieldCropsToSoilSightYears";
import { buildSilverModelMainCropSelectOptions } from "pages/app/scenario-analysis/utils/scenarioOptions";
import { useGetSoilSightOnboardingSetting } from "pages/app/scenario-onboarding/hooks/useSoilSightOnboardingSetting";
import { resolveFieldImportRoute } from "pages/app/scenario-onboarding/utils/onboardingState";

export const useSoilSightFieldImport = (fieldId: string | undefined) => {
  const { t } = useTranslation(["farm"]);
  const { currentFarm } = useGetFarms();
  const { user } = useUserContext();
  const { data: allFieldsPractices } = useGetAllFieldPracticesByFarm(currentFarm);
  const klimFarmId = currentFarm?.id ?? user?.farm?.id ?? "";
  const listProjectsQuery = useListSoilSightProjects(klimFarmId);

  const { isEnabled: isSoilSightIntegrationActive } = useFeatureFlag(FEATURE_FLAGS.SOIL_SIGHT_INTEGRATION);
  const { submitFieldImport, isSubmitting: isDirectImportSubmitting } = useSoilSightFieldImportSubmit();
  const { submitFieldImportViaOnboarding, isSubmitting: isOnboardingImportSubmitting } = useSoilSightFieldImportViaOnboarding();
  const isSoilSightImportSubmitting = isDirectImportSubmitting || isOnboardingImportSubmitting;

  // While the user is not onboarded (and not a grandfathered legacy user), the import routes
  // through the onboarding flow — read-only here, no grandfather lazy-write.
  const accessBase = useSoilSightScenariosAccessBase();
  const { onboardingState, isLoading: isOnboardingSettingLoading, isError: isOnboardingSettingError } = useGetSoilSightOnboardingSetting();
  const importRoute = resolveFieldImportRoute({
    isAccessLoading: accessBase.isLoading,
    isAccessError: accessBase.isError,
    isSettingLoading: isOnboardingSettingLoading,
    isSettingError: isOnboardingSettingError,
    hasProject: accessBase.hasProject,
    hasScenarios: accessBase.hasScenarios,
    onboardingState,
  });
  const isImportRoutingPending = importRoute === "pending";
  const shouldRouteImportThroughOnboarding = importRoute === "onboarding";
  const {
    data: practiceCropsForSoilSight,
    isFetching: isFetchingPracticeCropsForSoilSight,
    isLoading: isLoadingPracticeCropsForSoilSight,
  } = useGetPracticeCrops({ enabled: isSoilSightIntegrationActive });

  const fieldCrops = useMemo(() => (fieldId ? allFieldsPractices?.[fieldId]?.crops ?? [] : []), [allFieldsPractices, fieldId]);
  const fieldPractices = useMemo(() => (fieldId ? allFieldsPractices?.[fieldId]?.practices ?? [] : []), [allFieldsPractices, fieldId]);
  const fieldCropIds = useMemo(
    () => [
      ...new Set(
        fieldCrops
          .filter((crop) => crop.cropType === CROP_TYPE_FCPO.MAIN)
          .map((crop) => crop.cropId)
          .filter((cropId): cropId is string => Boolean(cropId?.trim()))
      ),
    ],
    [fieldCrops]
  );

  const mainCropOptions = useMemo(
    () => buildSilverModelMainCropSelectOptions(practiceCropsForSoilSight, fieldCropIds),
    [fieldCropIds, practiceCropsForSoilSight]
  );

  const importedCropYears = useMemo(
    () => reconcileYearsResidueLocks(mapFieldCropsToSoilSightYears(fieldCrops, fieldPractices, mainCropOptions), mainCropOptions),
    [fieldCrops, fieldPractices, mainCropOptions]
  );

  const canSubmitSoilSightImport = useMemo(
    () =>
      Boolean(currentFarm?.id && fieldId) &&
      !isFetchingPracticeCropsForSoilSight &&
      !isLoadingPracticeCropsForSoilSight &&
      !listProjectsQuery.isPending &&
      !isImportRoutingPending,
    [currentFarm?.id, fieldId, isFetchingPracticeCropsForSoilSight, isLoadingPracticeCropsForSoilSight, listProjectsQuery.isPending, isImportRoutingPending]
  );

  const handleSoilSightCTAClick = useCallback(
    async (e: MouseEvent<HTMLElement>) => {
      e.stopPropagation();
      if (!klimFarmId || !fieldId || !canSubmitSoilSightImport || isSoilSightImportSubmitting) return;

      let cropYears = importedCropYears;
      try {
        const cropRotation = await getFieldCropRotation({ fieldId });
        const detectedCropYears = reconcileYearsResidueLocks(
          mapCropRotationToSoilSightYears(cropRotation, fieldCrops, fieldPractices, mainCropOptions),
          mainCropOptions
        );
        if (detectedCropYears.length) cropYears = detectedCropYears;
      } catch {
        message({ content: t("soilSightPopup.importError"), icon: "error" });
      }

      if (shouldRouteImportThroughOnboarding) {
        await submitFieldImportViaOnboarding({ klimFarmId, fieldId, cropYears });
        return;
      }

      const hasExistingProjects = (listProjectsQuery.data?.length ?? 0) > 0;
      await submitFieldImport({ klimFarmId, fieldId, cropYears, hasExistingProjects });
    },
    [
      canSubmitSoilSightImport,
      fieldCrops,
      fieldId,
      fieldPractices,
      importedCropYears,
      isSoilSightImportSubmitting,
      klimFarmId,
      listProjectsQuery.data?.length,
      mainCropOptions,
      shouldRouteImportThroughOnboarding,
      submitFieldImport,
      submitFieldImportViaOnboarding,
      t,
    ]
  );
  return {
    isSoilSightIntegrationActive,
    canSubmitSoilSightImport,
    isSoilSightImportSubmitting,
    isFetchingPracticeCropsForSoilSight,
    handleSoilSightCTAClick,
  };
};
